import { BusinessNote, BusinessNoteLocation } from "common/types";
import { usePostResolveNote } from "common/hooks";
import { useState } from "react";
import ModalWithAction from "@/components/common/ModalWithAction";
import { StandardButton } from "../common/StandardButton";

type NoteCardProps = {
  note: BusinessNote;
  showLocationInfo?: boolean;
  businessId?: string;
  venueName?: string;
  onRefresh?: () => void;
  noteType?: string;
};

// Helper function to format date as relative time
const getRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMilliseconds = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears} year${diffInYears > 1 ? "s" : ""} ago`;
  } else if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`;
  } else if (diffInWeeks > 0) {
    return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`;
  } else if (diffInDays > 0) {
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes} min${diffInMinutes > 1 ? "s" : ""} ago`;
  } else {
    return "Just now";
  }
};

// Helper function to format date as"01 Aug 2025"
const getFormattedDate = (dateString: string): string => {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = String(date.getDate()).padStart(2, "0");
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

function NoteCard({
  note,
  showLocationInfo = true,
  businessId,
  venueName,
  onRefresh,
  noteType,
}: NoteCardProps) {
  const [isDismissDialogOpen, setIsDismissDialogOpen] = useState(false);
  const { mutate: resolveNote, status: resolveStatus } = usePostResolveNote();

  // Format the date as relative time
  // const formattedDate = getRelativeTime(note.business_notes_created_at);
  const formattedDate = getFormattedDate(note.business_notes_created_at);

  // Handler for dismissing the note
  const handleDismissNote = () => {
    if (!businessId) return;

    const businessAreaId = note.locationAreasData?.[0]?.businessNoteAreaId;
    if (!businessAreaId) return;

    resolveNote({
      noteId: note.business_notes_id,
      businessId,
      businessAreaId,
    });

    setIsDismissDialogOpen(false);

    // Call onRefresh after a short delay to allow the mutation to complete
    if (onRefresh) {
      setTimeout(() => {
        onRefresh();
      }, 500);
    }
  };

  // Helper function to get subtitle from location - extracts last part of category hierarchy
  const getSubTitle = (location: BusinessNoteLocation) => {
    if (location.locationName) {
      return location.locationName;
    }
    if (location.categoryHierarchy) {
      const parts = location.categoryHierarchy.split("/");
      if (parts.length > 1) {
        const part = parts.reverse()[0].trim();
        if (part.length > 0) {
          return part;
        } else {
          return location.categoryHierarchy;
        }
      } else {
        return location.categoryHierarchy;
      }
    }
    return "";
  };

  // Helper function to remove last category from hierarchy
  const removeLastCategory = (categoryHierarchy: string | undefined) => {
    if (!categoryHierarchy) {
      return ""; // Handle null/undefined/empty input
    }

    const lastSlashIndex = categoryHierarchy.lastIndexOf("/");

    if (lastSlashIndex === -1) {
      return categoryHierarchy;
    }

    return categoryHierarchy.substring(0, lastSlashIndex).trim();
  };

  return (
    <>
      <article className="bg-bg-brand-secondary-default rounded-lg border border-border-token-neutral-minimal p-4 md:p-6 hover:shadow-md transition-shadow">
        <h3 className="sr-only">
          {noteType === "Public" && note.business_note_title
            ? note.business_note_title
            : `Note by ${note.creator_user_name}`}
        </h3>
        {/* Two-column layout: Content area and Dismiss button */}
        <div className="flex items-start justify-between gap-4">
          {/* Content Area */}
          <div className="flex-1 min-w-0">
            <div className="label-s-medium text-fg-neutral-strong mb-2">
              {(noteType === "Public" &&
                note.schedule_date_from &&
                note.schedule_date_to) ||
              note.schedule_date_from ? (
                <div className="self-stretch justify-center text-fg-brand-subtle label-s-medium mb-1">
                  {note.schedule_date_from && note.schedule_date_to
                    ? `${getFormattedDate(note.schedule_date_from)} - ${getFormattedDate(note.schedule_date_to)}`
                    : `${getFormattedDate(note.schedule_date_from)} - No end date`}
                </div>
              ) : null}

              <span>{formattedDate} by </span>
              <span className="label-s-medium text-fg-neutral-strong">
                {note.creator_user_name}
              </span>
              {note.latest_editor_user_name && (
                <>
                  <span> • Edited by </span>
                  <span className="body-l-semibold text-fg-neutral-strong">
                    {note.latest_editor_user_name}
                  </span>
                </>
              )}

              {noteType === "Public" && (
                <div className="title-s-semibold text-fg-neutral-strong mb-2">
                  {note.business_note_title}
                </div>
              )}
            </div>
            {showLocationInfo &&
              note.locationAreasData &&
              note.locationAreasData.length > 0 &&
              (() => {
                // Deduplicate locations based on unique key combination
                const uniqueLocations = note.locationAreasData.filter(
                  (location, index, self) => {
                    const key = `${location.locationType}-${location.categoryIdHierarchy}`;
                    return (
                      index ===
                      self.findIndex(
                        (l) =>
                          `${l.locationType}-${l.categoryIdHierarchy}` === key,
                      )
                    );
                  },
                );

                return (
                  <div className="mb-3">
                    {uniqueLocations.map((location, index) => (
                      <div
                        key={`location-${index}`}
                        className="flex items-start gap-2 mb-2"
                      >
                        <div className="flex flex-col">
                          {location.locationType === "Business" ? (
                            <>
                              {noteType !== "Public" && (
                                <div className="title-s-semibold text-fg-neutral-strong mb-2">
                                  {venueName}
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <div className="w-4 h-4 flex-shrink-0">
                                  <img
                                    src="/images/MapPin.svg"
                                    alt="Location"
                                    className="w-4 h-4"
                                  />
                                </div>
                                <div className="label-s-medium text-fg-neutral-subtle">
                                  Whole venue
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="title-s-semibold text-fg-neutral-strong mb-2">
                                {location.locationName
                                  ? location.locationName
                                  : getSubTitle(location)}
                              </div>
                              {location.categoryHierarchy && (
                                <div className="flex items-center gap-1">
                                  <div className="w-4 h-4 flex-shrink-0">
                                    <img
                                      src="/images/MapPin.svg"
                                      alt="Location"
                                      className="w-4 h-4"
                                    />
                                  </div>
                                  <div className="label-s-medium text-fg-neutral-subtle">
                                    {location.locationName
                                      ? location.categoryHierarchy
                                      : removeLastCategory(
                                          location.categoryHierarchy,
                                        )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

            <p className="label-m-medium text-fg-neutral-strong mb-3">
              {note.notes}
            </p>
          </div>

          {/* Dismiss Button Area - Hidden for Public notes */}
          {noteType !== "Public" && (
            <div className="flex-shrink-0">
              <StandardButton
                onClick={() => {
                  setIsDismissDialogOpen(true);
                }}
                variant="icon"
                size="icon-xlg"
                className="px-3 py-1.5 label-m-medium border border-border-token-brand-minimal text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-blue"
              >
                <img
                  src="/images/CloseCircle.svg"
                  alt="Dismiss note"
                  className="mr-2 w-4 h-4"
                />
                {resolveStatus === "pending" ? "Dismissing..." : "Dismiss"}
              </StandardButton>
            </div>
          )}
        </div>
      </article>

      {/* Dismiss Confirmation Dialog */}
      <ModalWithAction
        isModalOpen={isDismissDialogOpen}
        title="Dismiss Note"
        subtitle="Are you sure you want to dismiss this note? This action cannot be undone."
        cancelButtonTitle="Cancel"
        actionButtonTitle={
          resolveStatus === "pending" ? "Dismissing..." : "Dismiss"
        }
        actionButtonType="warning"
        type="warning"
        onClose={() => setIsDismissDialogOpen(false)}
        onClick={handleDismissNote}
      />
    </>
  );
}

export default NoteCard;
